/**
 * ObstructionHistory
 *
 * "Historia" section of the obstruction details panel. Wraps the generic
 * `OperationHistory` component with the obstruction-specific config and a
 * thin query hook that fetches `GET /api/v1/obstructions/{id}/history`.
 * The backend endpoint lands with the follow-up history work — until then
 * the hook returns an empty event list so the timeline renders an empty
 * state rather than throwing.
 */
import { memo, useMemo, type ReactElement } from 'react';

import { OperationHistory } from '@/components/common/OperationHistory';
import type { Obstruction } from '@/types/obstructions';

import {
  mapObstructionEvent,
  useObstructionHistoryConfig,
} from './obstructionHistoryConfig';
import { useObstructionHistoryQuery } from './hooks/useObstructionHistoryQuery';

interface ObstructionHistoryProps {
  obstruction: Obstruction;
}

export const ObstructionHistory = memo(function ObstructionHistory({
  obstruction,
}: ObstructionHistoryProps): ReactElement {
  const config = useObstructionHistoryConfig();
  const { events, isLoading } = useObstructionHistoryQuery(obstruction.id);

  const mappedEvents = useMemo(
    () => events.map(mapObstructionEvent),
    [events]
  );

  return (
    <OperationHistory
      events={mappedEvents}
      config={config}
      isLoading={isLoading}
      showFilters
    />
  );
});
